'use client';

import {
  forwardRef,
  useState,
  type ChangeEvent,
  type SelectHTMLAttributes,
} from 'react';
import clsx from 'clsx';

interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

interface SelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'children'> {
  label?: string;
  error?: string;
  options: SelectOption[];
  placeholder?: string;
}

const Select = forwardRef<HTMLSelectElement, SelectProps>(
  ({ label, error, options, placeholder, className, id, ...props }, ref) => {
    const selectId =
      id ?? (label ? label.toLowerCase().replace(/\s+/g, '-') : undefined);

    /*
     * UI-08: the muted-placeholder styling used to be gated on `!props.value`,
     * which only holds for CONTROLLED usage. Used uncontrolled or with
     * `defaultValue` — as every `register()`-based react-hook-form usage is —
     * `props.value` is always undefined, so a genuinely selected option kept
     * rendering in `text-ink-muted` and a filled field looked empty or
     * disabled.
     *
     * Track the uncontrolled selection locally so "is the placeholder
     * currently selected?" is answerable in both modes.
     */
    const isControlled = props.value !== undefined;
    const [uncontrolledValue, setUncontrolledValue] = useState<string>(
      props.defaultValue != null ? String(props.defaultValue) : '',
    );
    const effectiveValue = isControlled
      ? String(props.value ?? '')
      : uncontrolledValue;
    const placeholderSelected = !!placeholder && effectiveValue === '';

    const handleChange = (event: ChangeEvent<HTMLSelectElement>) => {
      if (!isControlled) setUncontrolledValue(event.target.value);
      props.onChange?.(event);
    };

    return (
      <div className="flex flex-col gap-1.5">
        {label && (
          <label
            htmlFor={selectId}
            className="text-xs font-ui font-semibold uppercase tracking-wider text-ink-label"
          >
            {label}
          </label>
        )}
        <select
          ref={ref}
          id={selectId}
          className={clsx(
            'w-full rounded-[12px] bg-surface border border-outline-variant px-4 py-3 text-sm font-ui text-ink outline-none transition-all appearance-none',
            'focus:border-primary focus:ring-1 focus:ring-primary/20',
            'disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-surface-container',
            error && 'ring-1 ring-error/40',
            placeholderSelected && 'text-ink-muted',
            className,
          )}
          aria-invalid={error ? true : undefined}
          aria-describedby={error && selectId ? `${selectId}-error` : undefined}
          {...props}
          onChange={handleChange}
        >
          {placeholder && (
            <option value="" disabled className="text-ink-muted">
              {placeholder}
            </option>
          )}
          {options.map((opt) => (
            <option key={opt.value} value={opt.value} disabled={opt.disabled}>
              {opt.label}
            </option>
          ))}
        </select>
        {error && (
          <p
            id={selectId ? `${selectId}-error` : undefined}
            className="text-xs font-ui text-error"
            role="alert"
          >
            {error}
          </p>
        )}
      </div>
    );
  },
);
Select.displayName = 'Select';

export { Select };
export type { SelectProps, SelectOption };
